import React, {useState, useEffect} from 'react';
import {NavLink, useParams, useNavigate} from 'react-router-dom';
import {connect} from 'react-redux';
import {FormattedMessage, useIntl} from 'react-intl';
import {Form, message, Button, Modal} from 'antd';
import type {CheckboxOptionType} from 'antd/es/checkbox';

import methods from './methods';

import DefaultLayout from 'components/DefaultLayout';
import {CheckboxGroup, Input, Switch, Select, TimeZoneInput} from 'components/Form';
import Spinner from 'components/Spinner';
import {config} from 'config/config';
import {ComponentAccessCheck} from 'ui';
import useQueryApiClient from 'utils/useApiClient';

import type {LanguageInterface} from 'hooks/Language/types';
import type {CustomerProps} from 'pages/Customer/types';

interface CustomerFormInterface {
    customer?: CustomerProps;
    hideHeader?: boolean;
    session: any;
};

const formItemLayout = {
    labelCol: {span: 8},
    wrapperCol: {span: 6},
};

const mapStateToProps = (state: any) => {
    return {
        session: state.session,
    }
};

const CustomerForm: React.FC<CustomerFormInterface> = ({customer, hideHeader = false, session}) => {
    // needs rework to optimize
    const [showStatisticData, setShowStatisticData] = useState<boolean>(false);
    const [canEditSystemLanguages, setCanEditSystemLanguages] = useState<boolean>(false);

    const [form] = Form.useForm();
    const intl = useIntl();
    const navigate = useNavigate();
    const params = useParams();

    const customerId = params.id;
    const activeUserRole = session.active_user_type;
    const isCustomerAdmin = activeUserRole === 'CUSTOMER_ADMIN';
    const canEditCustomerAdmin = isCustomerAdmin && session.special_rights && session.special_rights.includes('can_manage_customers');

    const getCustomerApiUrl = isCustomerAdmin ? `/api/v1/user/${session.active_user_type_uuid}/organization/edit/${customerId}` : `${config.api.routes.backend.customers}/${customerId}?aa=123`;
    const createUpdateCustomerApiUrl = customerId ? (isCustomerAdmin ? `/api/v1/user/${session.active_user_type_uuid}/organization/edit/${customerId}/update` : `${config.api.routes.backend.customers}/${customerId}`) : config.api.routes.backend.customers;

    const {appendData: getCustomer, isLoading: getCustomerLoading} = useQueryApiClient({
        request: {
            url: getCustomerApiUrl,
            method: 'GET',
            disableOnMount: true,
        },
        onSuccess: (response: any): void => parseCustomerToForm(response.data),
    });

    const {data: customerIndustryTypes, appendData: getIndustryTypes, isLoading: getIndustryTypesLoading} = useQueryApiClient({
        request: {
            url: config.api.routes.enums.customerIndustryTypes,
            method: 'GET',
            disableOnMount: true,
        },
    });

    const {data: languages, isLoading: getLanguagesLoading} = useQueryApiClient({
        request: {
            url: config.api.routes.backend.languages,
            method: 'GET',
        },
        onSuccess: (response): void => {
            if (!customerId) {
                const initialSystemLanguages = response.languages.reduce((acc: string[], language: LanguageInterface) => {
                    if (language.publish) {
                        acc.push(language.code);
                    }

                    return acc;
                }, []);
                form.setFieldValue('systemLanguages', initialSystemLanguages);
            }
        },
    });

    // A new customer starts with the support contacts of whoever creates it as the phishing escalation address
    useQueryApiClient({
        request: {
            url: '/api/v1/support-settings/current',
            method: 'GET',
            disableOnMount: !!customerId,
        },
        onSuccess: (response: any): void => {
            if (!customerId && response?.supportEmail) {
                form.setFieldValue('phishingEscalationAddress', response.supportEmail);
            }
        },
    });

    const {appendData: createUpdateCustomer, isLoading: createUpdateCustomerLoading} = useQueryApiClient({
        request: {
            url: createUpdateCustomerApiUrl,
            method: customerId ? 'PUT' : 'POST',
        },
        onSuccess: (): void => {
            message.success(intl.formatMessage({id: 'general.submit_success'}));

            if (canEditCustomerAdmin) {
                navigate('/user/organizations');
            } else {
                navigate('/customers');
            }
        },
        onError: (error: any): void => {
            if (error.status === 422) {
                let errors = JSON.parse(error.message);
                form.setFields(methods.mapSubmitErrorsToForm(errors));
                
                if (!!errors.contactEmail?.length) {
                    Modal.error({
                        title: 'Error',
                        content: intl.formatMessage({id: "validation.customer_email_already_exists"})
                    });
                }
            }
        },
    });

    const parseCustomerToForm = (customer: CustomerProps): void => {
        form.setFieldsValue(methods.mapItemToValues(customer));
        setCanEditSystemLanguages(!!(customer.canEditSystemLanguages));
    }

    const selectedSystemLanguages: string[] = Form.useWatch('systemLanguages', form);

    const parseAvailableLanguages = (languages: LanguageInterface[]): CheckboxOptionType[] => {
        return languages.reduce((acc: CheckboxOptionType[], language) => {
            if ((selectedSystemLanguages ?? []).includes(language.code)) {
                acc.push({
                    label: language.name,
                    value: language.code,
                });
            }

            return acc;
        }, []);
    };

    const parseSystemLanguages = (languages: LanguageInterface[]): CheckboxOptionType[] => {
        const systemLanguages = languages.reduce((acc: CheckboxOptionType[], language) => {
            if (language.id) {
                acc.push({
                    label: language.name,
                    value: language.code,
                });
            }

            return acc;
        }, []);

        return systemLanguages;
    };

    const updatePrimaryLanguage = (newSelectedSystemLanguages: string): void => {
        const primaryLanguage = form.getFieldValue('defaultPrimaryLanguage');

        if (!newSelectedSystemLanguages.includes(primaryLanguage)) {
            form.setFieldValue('defaultPrimaryLanguage', null);
        }
    }

    const parseIndustryTypes = (industryTypes: any) => {
        let parsedIndustryTypes: any = {};

        Object.keys(industryTypes).map((el: string) => {
            return parsedIndustryTypes[el] = intl.formatMessage({id: `organizations.industry.${el.toLowerCase()}`});
        });

        return parsedIndustryTypes;
    };

    const submitForm = async (values: any) => createUpdateCustomer(methods.mapValuesForSubmit(values));

    useEffect(() => {
        if (customerId) {
            if (customer) {
                parseCustomerToForm(customer);
            } else {
                getCustomer();
            }
        }

        if (activeUserRole === 'DISTRIBUTOR_ADMIN' || activeUserRole === 'SUPER_ADMIN') {
            setShowStatisticData(true);
            getIndustryTypes();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId]);

    return <DefaultLayout.PageLayout withoutPageLayout={hideHeader} withStickyFooter={!hideHeader}>
        {!hideHeader &&
          <DefaultLayout.PageHeader
                breadcrumb={[{
                    name: intl.formatMessage({id: 'general.customers'}),
                    path: isCustomerAdmin ? '/user/organizations' : '/customers'
                }]}
                title={intl.formatMessage({id: customerId ? 'organization.edit_customer' : 'organization.add_customer'})}
            />
        }
        <Spinner spinning={getCustomerLoading || createUpdateCustomerLoading} opaque={getCustomerLoading}>
            <DefaultLayout.PageContent withTopPadding={!hideHeader} >
                <Form
                    form={form}
                    onFinish={submitForm}
                >
                    <Input
                        name='name'
                        label={intl.formatMessage({id: 'organization.organization_name'})}
                        validation={{required: true}}
                    />
                    <Input
                        name='legalName'
                        label={intl.formatMessage({id: 'organization.legal_name'})}
                        validation={{required: true}}
                    />
                    <Select
                        showSearch
                        filterOption={(input: any, option: any) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                        sortOptions={(a: any, b: any) => a[1].localeCompare(b[1])}
                        classifierIsObject={true}
                        name='country'
                        label={intl.formatMessage({id: 'general.country'})}
                        validation={{required: true}}
                        url={config.api.routes.enums.countries}
                    />
                    <Input
                        name='legalAddress'
                        label={intl.formatMessage({id: 'organization.legal_address'})}
                        validation={{required: true}}
                    />
                    <Input
                        name='officeAddress'
                        label={intl.formatMessage({id: 'organization.office_address'})}
                    />
                    <Input
                        name='vatNo'
                        label={intl.formatMessage({id: 'organization.vat_number'})}
                    />
                    <Input
                        name='bankAccountNo'
                        label={intl.formatMessage({id: 'organization.bank_account_number'})}
                    />
                    <Input
                        name='contactEmail'
                        label={intl.formatMessage({id: 'organization.business_contact_email'})}
                        validation={{required: true, type: 'email'}}
                    />
                    <Input
                        name='invoiceEmail'
                        label={intl.formatMessage({id: 'organization.invoice_email'})}
                        validation={{type: 'email'}}
                    />
                    <ComponentAccessCheck permission={'view_phishing'}>
                        <Input
                            name='phishingEscalationAddress'
                            label={intl.formatMessage({id: 'organization.phishing_email_escalation_address'})}
                            validation={{required: true, type: 'email'}}
                        />
                    </ComponentAccessCheck>
                    <Input
                        name='contactTelephone'
                        label={intl.formatMessage({id: 'organization.contact_telephone'})}
                        validation={{required: true}}
                    />
                    <Select
                        name='defaultPrimaryLanguage'
                        label={intl.formatMessage({id: 'organization.default_primary_language'})}
                        manualOptions={parseAvailableLanguages(languages?.languages ?? [])}
                        loading={getLanguagesLoading}
                        isResponseArray={true}
                        customLayout={formItemLayout}
                        customRules={[{required: true, message: intl.formatMessage({id: "validation.field_required"})}]}
                    />
                    <ComponentAccessCheck permission='can_customer_edit_environment'>
                        <CheckboxGroup
                            name='systemLanguages'
                            label={intl.formatMessage({id: 'system.languages.all'})}
                            options={parseSystemLanguages(languages?.languages ?? [])}
                            disabled={customerId ? !canEditSystemLanguages : false}
                            customLayout={formItemLayout}
                            onChange={updatePrimaryLanguage}
                        />
                    </ComponentAccessCheck>
                    <TimeZoneInput
                        name='defaultTimeZone'
                        label={intl.formatMessage({id: 'organization.default_time_zone'})}
                        customLayout={formItemLayout}
                        customRules={[{required: true, message: intl.formatMessage({id: "validation.field_required"})}]}
                    />
                    <Select
                        name='status'
                        disabled={isCustomerAdmin}
                        label={intl.formatMessage({id: 'organization.organization_status'})}
                        customRules={[{required: true, message: intl.formatMessage({id: "validation.field_required"})}]}
                        url={config.api.routes.enums.organizationStatuses}
                    />
                    {!isCustomerAdmin && (
                      <>
                        <Select
                          allowClear
                          name='managingOrganizationId'
                          label={intl.formatMessage({id: 'licensing.responsible_reseller'})}
                          url={config.api.routes.backend.selectOrganizations + '?status=ACTIVE'}
                          dataKey='organizations'
                          integerKey={true}
                          customRules={[{
                              required: true,
                              message: intl.formatMessage({id: "validation.field_required"})
                          }]}
                        />
                        <Switch
                          isFormItem
                          hasDefaultLayout
                          name='absenceMail'
                          label={intl.formatMessage({id: 'organization.absence_mail'})}
                        />
                        <Switch
                          isFormItem
                          hasDefaultLayout
                          name='waitForPayment'
                          label={intl.formatMessage({id: "organization.wait_for_payment_before_licence_activation"})}
                        />
                        <Switch
                          isFormItem
                          hasDefaultLayout
                          name='isOrganizationManager'
                          label={intl.formatMessage({id: "organization.education_center"})}
                        />
                        <Input
                          name='lockAccessAfterDays'
                          label={intl.formatMessage({id: 'organization.in_case_of_payment_delay_lock_access_to_content_after_days'})}
                          customRules={[{pattern: /^\d+$/, message: intl.formatMessage({id: "validation.must_be_integer"}),}]}
                        />
                        <Input
                          name='dataAnonymizationDays'
                          label={intl.formatMessage({id: 'data_anonymization_days'})}
                          customRules={[{pattern: /^\d+$/, message: intl.formatMessage({id: "validation.must_be_integer"}),}]}
                          placeholder="0"
                        />
                      </>
                    )}
                    {showStatisticData && (
                      <Select
                          allowClear
                          showSearch
                          name='industry'
                          label={intl.formatMessage({id: 'organization.industry'})}
                          manualOptions={parseIndustryTypes(customerIndustryTypes)}
                          loading={getIndustryTypesLoading}
                          onChange={(v?: string) => !v && form.setFieldValue('industry', null)}
                          customLayout={formItemLayout}
                          filterOption={(input: string, option: any) => {
                            return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                          }}
                      />
                    )}

                </Form>
            </DefaultLayout.PageContent>
            <DefaultLayout.PageFooter
                right={
                    <div className="form-buttons">
                        {isCustomerAdmin ?
                            <NavLink to="/user/organizations">
                                <Button className='outlined-button' style={{marginRight: 8}}>
                                    <FormattedMessage id="general.back"/>
                                </Button>
                            </NavLink>
                            :
                            <NavLink to="/customers">
                                <Button className='outlined-button' style={{marginRight: 8}}>
                                    <FormattedMessage id="general.back"/>
                                </Button>
                            </NavLink>
                        }
                        <Button
                            type="primary"
                            onClick={() => form.submit()}
                            loading={createUpdateCustomerLoading}
                        >
                            <FormattedMessage id="general.submit"/>
                        </Button>
                    </div>
                }
            />
        </Spinner>
    </DefaultLayout.PageLayout>
};

export default connect(mapStateToProps)(CustomerForm);
